import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { usePaymentMutations } from '../hooks/useSalesDocs';
import type { SalesDoc, PaymentMethod } from '../types';
import { localToday } from '@/shared/utils/utils';

interface RecordPaymentModalProps {
  open: boolean;
  invoice: SalesDoc | null;
  onClose: () => void;
}

const METHODS: { value: PaymentMethod; label: string }[] = [
  { value: 'UPI', label: 'UPI / QR Code' },
  { value: 'Bank Transfer', label: 'Net Banking / IMPS' },
  { value: 'Cash', label: 'Cash Payment' },
  { value: 'Credit Card', label: 'Credit/Debit Card' },
  { value: 'Cheque', label: 'Cheque Payment' },
];

export function RecordPaymentModal({ open, invoice, onClose }: RecordPaymentModalProps) {
  const { logPayment } = usePaymentMutations();
  const [amount, setAmount] = useState<number | ''>('');
  const [method, setMethod] = useState<PaymentMethod>('UPI');
  const [date, setDate] = useState(() => localToday());
  const [refNum, setRefNum] = useState('');
  const [notes, setNotes] = useState('');

  // Reset the form whenever the modal opens or the invoice changes, adjusting
  // state during render (via previous-value tracking) instead of in an effect.
  const [prevOpen, setPrevOpen] = useState(open);
  const [prevInvoice, setPrevInvoice] = useState(invoice);
  if (open !== prevOpen || invoice !== prevInvoice) {
    setPrevOpen(open);
    setPrevInvoice(invoice);
    if (open && invoice) {
      // Default to (and, below, cap at) the remaining balance rather than the
      // full original amount — client-approved audit fix. Mirrors
      // InvoiceList.tsx's working "Record Payment" modal (Accounts module),
      // which already does this against AccountsSalesInvoice.amountDue.
      // `amountDue` may be null/undefined for a doc the backend hasn't
      // computed it for yet (see types.ts); fall back to the full amount so
      // the field is never blocked from being usable.
      setAmount(invoice.amountDue ?? invoice.amount ?? '');
      setMethod('UPI');
      setDate(localToday());
      setRefNum('');
      setNotes('');
    }
  }

  // Remaining balance owed — used both as the input's max and for the
  // over-payment guard below. Falls back to the full amount for a doc whose
  // amountDue hasn't been computed yet, same as the default above.
  const remainingDue = invoice?.amountDue ?? invoice?.amount ?? 0;
  const numAmount = Number(amount) || 0;
  const excessAmount = remainingDue > 0 ? Math.max(0, numAmount - remainingDue) : numAmount;
  const isOverpayment = remainingDue > 0 && numAmount > remainingDue;
  const [retainExcessAsAdvance, setRetainExcessAsAdvance] = useState(true);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!invoice) return;
    if (!amount || Number(amount) <= 0) {
      toast.error('Please enter a valid amount.');
      return;
    }
    if (remainingDue > 0 && Number(amount) > remainingDue && !retainExcessAsAdvance) {
      toast.error(`Amount exceeds outstanding balance of ₹${remainingDue.toLocaleString('en-IN')}. Enable "Retain excess as advance" to proceed.`);
      return;
    }
    if (!date) {
      toast.error('Please select a payment date.');
      return;
    }

    logPayment.mutate(
      {
        invoice_id: invoice.id,
        amount_paid: Number(amount),
        payment_method: method,
        payment_date: date,
        transaction_reference: refNum.trim(),
        notes: notes.trim(),
        retainExcessAsAdvance: isOverpayment || remainingDue <= 0 ? retainExcessAsAdvance : false,
      },
      {
        onSuccess: () => {
          toast.success(
            excessAmount > 0 && retainExcessAsAdvance
              ? `Payment logged: ₹${Math.min(numAmount, remainingDue).toLocaleString('en-IN')} applied to invoice, ₹${excessAmount.toLocaleString('en-IN')} retained as advance balance.`
              : 'Payment logged successfully.'
          );
          onClose();
        },
        onError: (err: any) => {
          toast.error(err.response?.data?.error || 'Failed to log payment.');
        },
      }
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={remainingDue <= 0 ? 'Record Customer Advance' : 'Record Payment / Advance'}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={logPayment.isPending}>
            Cancel
          </Button>
          <Button type="submit" form="payment-form" loading={logPayment.isPending}>
            {remainingDue <= 0 ? 'Save Advance' : isOverpayment ? 'Save Payment & Advance' : 'Save Payment'}
          </Button>
        </>
      }
    >
      <form id="payment-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="rounded bg-surface-container-low p-3 border border-outline/30">
          <span className="text-body-sm text-on-surface-variant block">Invoice Title</span>
          <span className="font-semibold text-on-surface text-data-md block">{invoice?.title}</span>
          <div className="flex gap-4 mt-2">
            <div>
              <span className="text-data-xs text-on-surface-variant block uppercase font-semibold">Total Amount</span>
              <span className="font-bold text-primary text-data-lg">₹{invoice?.amount?.toLocaleString('en-IN') || 0}</span>
            </div>
            <div>
              <span className="text-data-xs text-on-surface-variant block uppercase font-semibold">Balance Due</span>
              <span className={`font-bold text-data-lg ${remainingDue > 0 ? 'text-danger' : 'text-success'}`}>
                ₹{remainingDue.toLocaleString('en-IN')}
              </span>
            </div>
            <div>
              <span className="text-data-xs text-on-surface-variant block uppercase font-semibold">Reference</span>
              <span className="text-on-surface text-data-md block font-medium">{invoice?.reference_number || '—'}</span>
            </div>
          </div>
        </div>

        {/* Amount Paid / Advance */}
        <Input
          label={remainingDue <= 0 ? 'Advance Amount (₹) *' : 'Amount Paid (₹) *'}
          name="amount_paid"
          type="number"
          min={1}
          step="any"
          required
          value={amount}
          onChange={(e) => setAmount(e.target.value ? Number(e.target.value) : '')}
          placeholder="0"
        />

        {/* Informative breakdown when overpayment or pure advance */}
        {numAmount > 0 && isOverpayment && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900 text-body-sm space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-amber-950">Additional / Excess Amount</span>
              <span className="font-bold text-amber-800 text-data-md">+₹{excessAmount.toLocaleString('en-IN')}</span>
            </div>
            <p className="text-body-xs text-amber-800">
              ₹{remainingDue.toLocaleString('en-IN')} will clear the outstanding invoice balance. The excess ₹{excessAmount.toLocaleString('en-IN')} will be held on account as customer credit / advance balance.
            </p>
            <label className="flex items-center gap-2 cursor-pointer pt-1 border-t border-amber-200">
              <input
                type="checkbox"
                className="rounded border-amber-400 text-primary focus:ring-primary h-4 w-4"
                checked={retainExcessAsAdvance}
                onChange={(e) => setRetainExcessAsAdvance(e.target.checked)}
              />
              <span className="text-body-xs font-medium text-amber-950">
                Retain excess ₹{excessAmount.toLocaleString('en-IN')} as customer advance balance with us
              </span>
            </label>
          </div>
        )}

        {numAmount > 0 && remainingDue <= 0 && (
          <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-blue-900 text-body-sm space-y-1">
            <p className="font-semibold text-blue-950">Advance Payment Notice</p>
            <p className="text-body-xs text-blue-800">
              This invoice has no outstanding balance. The full payment of <strong>₹{numAmount.toLocaleString('en-IN')}</strong> will be credited and held as advance balance for the customer.
            </p>
          </div>
        )}

        {/* Date + Method Row */}
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Payment Date *"
            name="payment_date"
            type="date"
            required
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <Select
            label="Payment Method *"
            name="payment_method"
            value={method}
            onChange={(e) => setMethod(e.target.value as PaymentMethod)}
            options={METHODS}
          />
        </div>

        {/* Transaction Ref */}
        <Input
          label="Transaction Ref / Chq No."
          name="ref_num"
          value={refNum}
          onChange={(e) => setRefNum(e.target.value)}
          placeholder="Txn ID, Cheque number…"
        />

        {/* Notes */}
        <div>
          <label htmlFor="payment-notes" className="mb-1.5 block text-body-sm font-medium text-on-surface">
            Private Notes
          </label>
          <textarea
            id="payment-notes"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any internal remarks…"
            className="w-full resize-none rounded border border-outline bg-surface px-3 py-2 text-data-md text-on-surface placeholder:text-on-surface-variant/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>
      </form>
    </Modal>
  );
}
